/* eslint-disable no-restricted-syntax */
import { useDataModel } from '@thd-nucleus/data-sources';
import {
  getContentfulTargetingIds,
} from '@thd-olt-component-react/component-selector';
import { createGraphFromLayoutOperations } from '../Orchestration/layoutUtils';
import { transformDataFromContentful } from '../Orchestration/dataUtils';

const useLayoutOrchestration = ({
  variables,
  layoutDataModel,
  layoutOptions,
  ssr,
  customerType
}) => {
  const { myComponents, componentOptions, layoutOperations } = layoutOptions;
  let queryName;
  if (!variables.slug) {
    queryName = 'layout';
  } else {
    queryName = 'layouts';
  }

  // Server SIDE DATA MODEL
  const contentfulServerSideLayouts = useDataModel(queryName, {
    variables,
    skip: !variables.slug && !variables.layoutClass,
    ssr: true,
    dataModel: layoutDataModel.server,
  });

  // CLIENT SIDE DATA MODEL
  const contentfulClientSideLayouts = useDataModel(queryName, {
    variables,
    skip: customerType === 'b2c-guest' || (!variables.slug && !variables.layoutClass),
    ssr: false,
    dataModel: layoutDataModel.client,
  });

  const contentfulServerSideLoading = contentfulServerSideLayouts.loading;
  const contentfulServerSideError = contentfulServerSideLayouts.error;
  const contentfulServerSideSEO = contentfulServerSideLayouts.data?.layouts?.seo;

  const contentfulClientSideLoading = contentfulClientSideLayouts.loading;
  const contentfulClientSideError = contentfulClientSideLayouts.error;
  const contentfulClientSideSEO = contentfulClientSideLayouts.data?.layouts?.seo;

  let combinedLayoutData;
  if (contentfulServerSideLayouts.data && contentfulClientSideLayouts.data) {
    combinedLayoutData = !variables.slug
      ? { ...contentfulServerSideLayouts.data?.layout, ...contentfulClientSideLayouts.data?.layout }
      : { ...contentfulServerSideLayouts.data?.layouts?.content, ...contentfulClientSideLayouts.data.layouts?.content };
  }

  const contentfulComponents = (combinedLayoutData
    || (!variables.slug
      ? contentfulServerSideLayouts?.data?.layout
      : contentfulServerSideLayouts?.data?.layouts?.content)
    || (!variables.slug
      ? contentfulClientSideLayouts?.data?.layout
      : contentfulClientSideLayouts?.data?.layouts?.content)
  );

  const { mboxes, campaignIds } = getContentfulTargetingIds({
    content: contentfulComponents,
  });

  let isReady = combinedLayoutData ? (mboxes.length > 0 || campaignIds.length > 0) : false;

  if (customerType === 'b2c-guest') {
    isReady = contentfulComponents ? (mboxes.length > 0 || campaignIds.length > 0) : false;
  }

  const layout = contentfulComponents
    ? transformDataFromContentful([], contentfulComponents)
    : null;

  if (!layout) {
    return {
      data: null,
      loading: { server: contentfulServerSideLoading, client: contentfulClientSideLoading },
      error: { server: contentfulServerSideError, client: contentfulClientSideError },
    };
  }

  const sharedLayoutInfo = {
    allComponents: layout,
    myComponents,
    componentOptions,
    layoutOperations,
  };

  const { graph: minWidthLargeGraph, components: minWidthLargeComponents } = createGraphFromLayoutOperations({
    ...sharedLayoutInfo,
    mediaQuery: 'minWidthLarge',
  });

  const { graph: minWidthMediumGraph, components: minWidthMediumComponents } = createGraphFromLayoutOperations({
    ...sharedLayoutInfo,
    allComponents: minWidthLargeComponents,
    mediaQuery: 'minWidthMedium',
  });

  const { graph: maxWidthMediumGraph } = createGraphFromLayoutOperations({
    ...sharedLayoutInfo,
    allComponents: minWidthMediumComponents,
    mediaQuery: 'maxWidthMedium',
  });

  return {
    data: {
      components: Object.values(maxWidthMediumGraph).flatMap(
        (rowComponents) => rowComponents,
      ),
      layoutGraphs: {
        maxWidthMedium: maxWidthMediumGraph,
        minWidthMedium: minWidthMediumGraph,
        minWidthLarge: minWidthLargeGraph,
      },
      seo: { ...contentfulServerSideSEO, ...contentfulClientSideSEO } || null,
      mboxes,
      campaignIds,
      isReady,
    },
    loading: { server: contentfulServerSideLoading, client: contentfulClientSideLoading },
    error: { server: contentfulServerSideError, client: contentfulClientSideError },
  };
};

export default useLayoutOrchestration;
